import { CalendarClock, Sparkles, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { DomainCard, Eyebrow, IconTile, cn, domainClasses } from "@appcaire/ui";
import { howItWorksCopy } from "./howItWorksCopy";
import type { LandingLocale } from "../landingCopy";
import {
  homeSectionClass,
  homeSectionHeadClass,
  homeSectionTitleClass,
} from "./HomeParts";

const WEEKS = [41, 42];
const FIRST_DAY = 5; // Monday 5 October 2026

/** The cleaning exception: a fixed day that moves only when staff are short (3c). */
export function ServiceExceptionSection({ locale }: { locale: LandingLocale }) {
  const c = howItWorksCopy[locale].exception;
  const [shortage, setShortage] = useState(false);
  return (
    <section
      className={homeSectionClass}
      aria-labelledby="exception-heading"
      data-how-section="exception"
    >
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-[60ch]">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 id="exception-heading" className={homeSectionTitleClass}>
            {c.title}
          </h2>
          <p className="mt-3">{c.lead}</p>
        </div>
        <button
          type="button"
          onClick={() => setShortage(!shortage)}
          className={cn(
            "inline-flex min-h-11 items-center gap-2 rounded-xl border-[1.5px] px-4 py-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-caire-ink",
            shortage
              ? "border-caire-ink bg-caire-surface text-caire-ink"
              : "border-domain-attention-solid bg-domain-attention-tint text-domain-attention-ink",
          )}
        >
          <TriangleAlert aria-hidden="true" className="h-4 w-4" />
          {shortage ? c.reset : c.simulate}
        </button>
      </div>
      <ul className="mt-6 flex flex-wrap gap-2" role="list">
        {c.facts.map((fact) => (
          <li
            key={fact}
            className="rounded-full bg-caire-sand px-3 py-1.5 font-mono text-xs font-semibold text-caire-ink"
          >
            {fact}
          </li>
        ))}
      </ul>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[40rem] border-separate border-spacing-2 text-left">
          <thead>
            <tr>
              <th scope="col" className="w-16" />
              {c.weekdays.map((day) => (
                <th
                  key={day}
                  scope="col"
                  className="font-mono text-xs font-semibold uppercase tracking-[0.08em] text-caire-muted"
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {WEEKS.map((week, w) => (
              <tr key={week}>
                <th
                  scope="row"
                  className="font-mono text-xs font-semibold text-caire-muted"
                >
                  {c.weekLabel(week)}
                </th>
                {c.weekdays.map((day, d) => {
                  const date = FIRST_DAY + w * 7 + d;
                  const isUsual = w === 0 && d === 1;
                  const isMoved = w === 1 && d === 2;
                  const limited = shortage && isUsual;
                  const clean = shortage ? isMoved : isUsual;
                  return (
                    <td
                      key={day}
                      className={cn(
                        "h-28 rounded-xl p-2 align-top",
                        limited
                          ? "border-[1.5px] border-domain-attention-solid bg-domain-attention-tint"
                          : clean
                            ? "border-2 border-domain-human-solid bg-caire-surface"
                            : "border border-caire-hairline bg-caire-surface",
                      )}
                    >
                      <div className="flex items-center justify-between font-mono text-[11px] text-caire-muted">
                        <span>{`${day} ${date}/10`}</span>
                        {limited ? (
                          <span className="font-semibold uppercase text-domain-attention-ink">
                            {c.limited}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1.5 rounded-md bg-domain-care-tint px-2 py-1 text-xs font-semibold text-domain-care-ink">
                        {c.breakfast}
                      </div>
                      {clean ? (
                        <div
                          className={cn(
                            "mt-1.5 flex flex-col rounded-md px-2 py-1 text-xs font-semibold",
                            shortage
                              ? cn(
                                  domainClasses.planning.solid,
                                  "text-caire-on-solid",
                                )
                              : "bg-domain-human-tint text-domain-human-ink",
                          )}
                        >
                          <span>{c.cleaning}</span>
                          <span className="font-mono font-medium">
                            {shortage ? c.moved : "14:10–15:40"}
                          </span>
                        </div>
                      ) : null}
                      {limited ? (
                        <div className="mt-1.5 rounded-md border border-dashed border-domain-attention-solid px-2 py-1 text-xs text-domain-attention-ink">
                          {c.previous}
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div aria-live="polite" className="mt-4">
        <DomainCard
          domain={shortage ? "planning" : "society"}
          surface="tint"
          density="product"
          className="flex-row items-start gap-3 shadow-none"
        >
          <IconTile
            icon={shortage ? Sparkles : CalendarClock}
            domain={shortage ? "planning" : "society"}
            size="xs"
            variant="tint"
          />
          <p className="text-[15px] leading-normal text-caire-ink">
            {shortage ? c.shortage : c.normal}
          </p>
        </DomainCard>
      </div>
      <p className="mt-2 text-xs text-caire-muted">{c.example}</p>
    </section>
  );
}
